import { clsx } from 'clsx';
import type { ReactNode } from 'react';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<Size, number> = { xs: 20, sm: 28, md: 36, lg: 48, xl: 64 };
const SIZE_TEXT: Record<Size, string> = {
  xs: 'text-[9px]',
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm',
  xl: 'text-base',
};

const FALLBACK_BG = 'linear-gradient(135deg, #27272a, #18181b)';

function initials(name?: string): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export interface AvatarProps {
  size?: Size;
  src?: string;
  name?: string;
  className?: string;
}

export function Avatar({ size = 'md', src, name, className }: AvatarProps) {
  const px = SIZE_PX[size];
  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center rounded-full overflow-hidden border-2 border-bg-primary text-fg-primary font-bold',
        SIZE_TEXT[size],
        className,
      )}
      style={{
        width: px,
        height: px,
        background: src ? undefined : FALLBACK_BG,
      }}
    >
      {src ? (
        <img src={src} alt={name ?? ''} className="h-full w-full object-cover" />
      ) : (
        <span>{initials(name)}</span>
      )}
    </span>
  );
}

export interface AvatarGroupProps {
  max?: number;
  size?: Size;
  className?: string;
  children: ReactNode;
}

export function AvatarGroup({ max = 4, size = 'md', className, children }: AvatarGroupProps) {
  const arr = Array.isArray(children) ? children : [children];
  const visible = arr.slice(0, max);
  const overflow = arr.length - visible.length;
  const px = SIZE_PX[size];

  return (
    <div className={clsx('inline-flex items-center', className)}>
      {visible.map((c, i) => (
        <span key={i} className={i > 0 ? '-ml-2' : ''}>
          {c}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className={clsx(
            '-ml-2 inline-flex items-center justify-center rounded-full bg-accent-bg text-accent border-2 border-bg-primary font-bold',
            SIZE_TEXT[size],
          )}
          style={{ width: px, height: px }}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
